/**
 * STOCK-BROKING CRM — DEMO DATA MODULE
 * ------------------------------------
 * Self-contained, client-side mock data for the stock-broking demo variant.
 * There is NO backend, NO database and NO real API keys involved: every
 * "client", KYC status, brokerage number, WhatsApp message and audit-log line
 * below is fabricated seed data. The dashboard keeps this in React state and
 * "Reset Demo" simply re-clones the seed via `cloneSeedLeads()`.
 *
 * See ./README notes in src/components/demos/stock-broking/README.md for the
 * mock-vs-production breakdown.
 */

/* ------------------------------------------------------------------ *
 * Pipeline funnel (demat account-opening + activation)
 * ------------------------------------------------------------------ */

export type BrokingStage =
  | "New Enquiry"
  | "KYC (PAN / Aadhaar / IPV)"
  | "Bank & Nominee"
  | "E-Sign"
  | "Account Activated"
  | "Segment Activation"
  | "Active Trader"
  | "Dormant / Recall";

export interface BrokingStageMeta {
  id: string;
  name: BrokingStage;
  short: string;
  dot: string; // tailwind bg for the header dot
  accent: string; // text accent
  description: string;
}

export const BROKING_STAGES: BrokingStageMeta[] = [
  {
    id: "new_enquiry",
    name: "New Enquiry",
    short: "Enquiry",
    dot: "bg-slate-400",
    accent: "text-slate-300",
    description: "Fresh account-opening lead captured from web / referral / IB.",
  },
  {
    id: "kyc",
    name: "KYC (PAN / Aadhaar / IPV)",
    short: "KYC",
    dot: "bg-amber-400",
    accent: "text-amber-300",
    description: "PAN verified, Aadhaar e-KYC and In-Person Verification (IPV).",
  },
  {
    id: "bank_nominee",
    name: "Bank & Nominee",
    short: "Bank",
    dot: "bg-orange-400",
    accent: "text-orange-300",
    description: "Bank penny-drop, nominee declaration and income proof.",
  },
  {
    id: "esign",
    name: "E-Sign",
    short: "E-Sign",
    dot: "bg-sky-400",
    accent: "text-sky-300",
    description: "Aadhaar OTP e-Sign of the account opening form (AOF).",
  },
  {
    id: "activated",
    name: "Account Activated",
    short: "Activated",
    dot: "bg-blue-400",
    accent: "text-blue-300",
    description: "Demat + trading account live; UCC mapped at exchange.",
  },
  {
    id: "segment_activation",
    name: "Segment Activation",
    short: "Segments",
    dot: "bg-violet-400",
    accent: "text-violet-300",
    description: "F&O / Commodity / Currency segment enabled after proof.",
  },
  {
    id: "active_trader",
    name: "Active Trader",
    short: "Active",
    dot: "bg-emerald-400",
    accent: "text-emerald-300",
    description: "Funded, trading regularly and contributing brokerage.",
  },
  {
    id: "dormant",
    name: "Dormant / Recall",
    short: "Dormant",
    dot: "bg-rose-400",
    accent: "text-rose-300",
    description: "No trades in 90+ days — re-activation outreach queued.",
  },
];

export const STAGE_ORDER: BrokingStage[] = BROKING_STAGES.map((s) => s.name);

/** Stages that open the guided StageModal (they capture required inputs). */
export const GUIDED_STAGES: BrokingStage[] = [
  "Account Activated",
  "Segment Activation",
  "Active Trader",
];

/* ------------------------------------------------------------------ *
 * Trading segments
 * ------------------------------------------------------------------ */

export interface BrokingSegment {
  id: string;
  label: string;
  short: string;
  exchange: string;
  color: string;
  requiresProof: boolean;
}

export const BROKING_SEGMENTS: BrokingSegment[] = [
  { id: "equity_delivery", label: "Equity Delivery (CNC)", short: "Equity", exchange: "NSE · BSE", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25", requiresProof: false },
  { id: "intraday", label: "Intraday (MIS)", short: "Intraday", exchange: "NSE · BSE", color: "bg-blue-500/15 text-blue-300 border-blue-500/25", requiresProof: false },
  { id: "fno", label: "Futures & Options", short: "F&O", exchange: "NSE", color: "bg-violet-500/15 text-violet-300 border-violet-500/25", requiresProof: true },
  { id: "commodity", label: "Commodity", short: "MCX", exchange: "MCX", color: "bg-amber-500/15 text-amber-300 border-amber-500/25", requiresProof: true },
  { id: "currency", label: "Currency Derivatives", short: "CDS", exchange: "NSE", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25", requiresProof: true },
  { id: "mtf", label: "Margin Trading (MTF)", short: "MTF", exchange: "NSE · BSE", color: "bg-rose-500/15 text-rose-300 border-rose-500/25", requiresProof: true },
  { id: "mf_sip", label: "Mutual Funds / SIP", short: "MF · SIP", exchange: "AMFI", color: "bg-teal-500/15 text-teal-300 border-teal-500/25", requiresProof: false },
];

export function segmentMeta(idOrLabel: string): BrokingSegment | undefined {
  return BROKING_SEGMENTS.find((s) => s.id === idOrLabel || s.short === idOrLabel || s.label === idOrLabel);
}

/* ------------------------------------------------------------------ *
 * Branches & Relationship Managers (RM)
 * ------------------------------------------------------------------ */

export interface Branch {
  id: string;
  name: string;
  city: string;
  code: string;
  head: string;
}

export const BRANCHES: Branch[] = [
  { id: "blr", name: "Bengaluru (HQ)", city: "Bengaluru", code: "BR-BLR-01", head: "Meera Krishnan" },
  { id: "mum", name: "Mumbai — Fort", city: "Mumbai", code: "BR-MUM-02", head: "Rohan Deshpande" },
  { id: "del", name: "Delhi NCR — CP", city: "New Delhi", code: "BR-DEL-03", head: "Simran Kaur" },
  { id: "hyd", name: "Hyderabad — Banjara", city: "Hyderabad", code: "BR-HYD-04", head: "Kiran Reddy" },
  { id: "ahm", name: "Ahmedabad — CG Road", city: "Ahmedabad", code: "BR-AHM-05", head: "Jignesh Patel" },
];

export interface RelationshipManager {
  id: string;
  name: string;
  branchId: string;
  role: string;
  arn?: string; // AMFI Registration Number for MF distribution
  rating: string;
  accountsMtd: number;
  conversion: string;
}

export const RELATIONSHIP_MANAGERS: RelationshipManager[] = [
  { id: "RM-101", name: "Aditya Nair", branchId: "blr", role: "Sr. Relationship Manager", arn: "ARN-184422", rating: "4.9/5", accountsMtd: 38, conversion: "31%" },
  { id: "RM-102", name: "Priya Iyer", branchId: "blr", role: "Equity Advisory RM", arn: "ARN-190871", rating: "4.8/5", accountsMtd: 29, conversion: "27%" },
  { id: "RM-103", name: "Vikram Malhotra", branchId: "mum", role: "F&O Dealer / RM", rating: "4.85/5", accountsMtd: 41, conversion: "34%" },
  { id: "RM-104", name: "Neha Agarwal", branchId: "del", role: "Wealth & SIP RM", arn: "ARN-201355", rating: "4.7/5", accountsMtd: 22, conversion: "24%" },
  { id: "RM-105", name: "Sandeep Rao", branchId: "hyd", role: "Commodity Desk RM", rating: "4.75/5", accountsMtd: 18, conversion: "22%" },
  { id: "RM-106", name: "Ananya Shah", branchId: "ahm", role: "Relationship Manager", arn: "ARN-211908", rating: "4.8/5", accountsMtd: 25, conversion: "26%" },
];

export function rmById(id: string): RelationshipManager | undefined {
  return RELATIONSHIP_MANAGERS.find((r) => r.id === id);
}
export function branchById(id: string): Branch | undefined {
  return BRANCHES.find((b) => b.id === id);
}
export function rmsForBranch(branchId: string): RelationshipManager[] {
  return RELATIONSHIP_MANAGERS.filter((r) => r.branchId === branchId);
}

/* ------------------------------------------------------------------ *
 * Roles (role-based access demo)
 * ------------------------------------------------------------------ */

export type CrmRole = "Admin" | "Management" | "RM";

export const CRM_ROLES: { id: CrmRole; label: string; scope: string }[] = [
  { id: "Admin", label: "Admin", scope: "Full access · all branches · compliance & audit" },
  { id: "Management", label: "Management", scope: "All branches · MIS & reports · no PAN edit" },
  { id: "RM", label: "Relationship Manager", scope: "Own assigned leads only · masked PAN" },
];

/* ------------------------------------------------------------------ *
 * Brokerage plans (client portal pricing)
 * ------------------------------------------------------------------ */

export interface BrokeragePlan {
  id: string;
  title: string;
  tagline: string;
  amc: number; // annual maintenance charge ₹
  deliveryBrokerage: string;
  intradayBrokerage: string;
  fnoBrokerage: string;
  badge: string;
  features: string[];
  highlight?: boolean;
}

export const BROKERAGE_PLANS: BrokeragePlan[] = [
  {
    id: "value",
    title: "Value Investor",
    tagline: "Zero-brokerage long-term equity delivery for wealth builders.",
    amc: 300,
    deliveryBrokerage: "₹0 (Free Delivery)",
    intradayBrokerage: "₹20 / order",
    fnoBrokerage: "₹20 / order",
    badge: "Most Popular",
    highlight: true,
    features: [
      "Free equity delivery — pay ₹0 brokerage",
      "Flat ₹20 per intraday & F&O executed order",
      "Free direct Mutual Fund & SIP investing",
      "Advanced charting with 100+ indicators",
    ],
  },
  {
    id: "active",
    title: "Active Trader",
    tagline: "Low flat fees and priority margins for high-frequency F&O traders.",
    amc: 500,
    deliveryBrokerage: "₹0 (Free Delivery)",
    intradayBrokerage: "₹15 / order",
    fnoBrokerage: "₹15 / order",
    badge: "Best for F&O",
    features: [
      "Discounted ₹15 flat per intraday & F&O order",
      "Priority MTF funding up to 4x margin",
      "Dedicated F&O dealing desk & basket orders",
      "Real-time margin & MTM position tracking",
    ],
  },
  {
    id: "wealth",
    title: "Wealth Partner",
    tagline: "Advisory-led equity, SIP and portfolio management with a dedicated RM.",
    amc: 999,
    deliveryBrokerage: "0.10% Delivery",
    intradayBrokerage: "0.02% Intraday",
    fnoBrokerage: "₹20 / order",
    badge: "Advisory",
    features: [
      "Dedicated Relationship Manager & research desk",
      "Curated equity baskets & goal-based SIP planning",
      "Quarterly portfolio review & rebalancing",
      "Priority WhatsApp support & IPO allotment desk",
    ],
  },
];

export function calculateAnnualCost(
  planId: string,
  monthlyOrders: number,
  sipMonthly: number
): { plan: BrokeragePlan; annualBrokerage: number; annualAmc: number; annualSip: number; total: number } {
  const plan = BROKERAGE_PLANS.find((p) => p.id === planId) || BROKERAGE_PLANS[0];
  // Very rough indicative model for the demo estimator only.
  const perOrder = plan.id === "active" ? 15 : plan.id === "wealth" ? 25 : 20;
  const annualBrokerage = perOrder * monthlyOrders * 12;
  const annualAmc = plan.amc;
  const annualSip = sipMonthly * 12;
  return {
    plan,
    annualBrokerage,
    annualAmc,
    annualSip,
    total: annualBrokerage + annualAmc,
  };
}

/* ------------------------------------------------------------------ *
 * Lead / client record + activity timeline
 * ------------------------------------------------------------------ */

export type ActivityType = "system" | "stage" | "kyc" | "call" | "whatsapp" | "note" | "trade";

export interface ActivityEvent {
  id: string;
  ts: string; // human friendly
  type: ActivityType;
  title: string;
  detail: string;
  actor: string;
}

export interface BrokingLead {
  id: number;
  client_code: string;
  name: string;
  pan: string;
  email: string;
  phone: string;
  city: string;
  branch_id: string;
  rm_id: string;
  stage: BrokingStage;
  priority: "HOT" | "WARM" | "COLD";
  plan_id: string;
  source: string;

  // KYC / onboarding checklist
  kyc_status: "Pending" | "PAN Verified" | "IPV Done" | "Verified" | "Rejected";
  aadhaar_linked: boolean;
  ipv_done: boolean;
  bank_verified: boolean;
  nominee_added: boolean;
  esign_done: boolean;
  demat_no: string | null; // NSDL DP + BO id

  // segments
  segments_active: string[]; // segment ids
  segments_requested: string[];

  // money
  aum: number; // portfolio / assets value ₹
  margin_available: number; // ₹
  brokerage_mtd: number; // ₹ brokerage revenue this month
  sip_active: boolean;
  sip_amount: number; // ₹ / month

  disposition: "interested" | "callback" | "docs_pending" | "funded" | "dnd";
  last_activity: string;
  notes: string;
  timeline: ActivityEvent[];
  created_at: string;
  updated_at: string;
}

/* ------------------------------------------------------------------ *
 * Seed clients
 * ------------------------------------------------------------------ */

const RAW_SEED: Omit<BrokingLead, "id">[] = [
  {
    client_code: "JS-BR-1042",
    name: "Rajesh Kumar",
    pan: "AXKPR4821L",
    email: "rajesh.kumar@gmail.com",
    phone: "+91 98860 41042",
    city: "Bengaluru",
    branch_id: "blr",
    rm_id: "RM-101",
    stage: "Active Trader",
    priority: "HOT",
    plan_id: "active",
    source: "Google — Free Demat",
    kyc_status: "Verified",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: true,
    esign_done: true,
    demat_no: "IN300214 · 18294471",
    segments_active: ["equity_delivery", "intraday", "fno"],
    segments_requested: ["commodity"],
    aum: 1840000,
    margin_available: 620000,
    brokerage_mtd: 24800,
    sip_active: true,
    sip_amount: 25000,
    disposition: "funded",
    last_activity: "Placed 14 F&O orders today",
    notes: "High-value F&O trader. Requested MCX commodity activation — income proof pending review.",
    timeline: [
      { id: "t1", ts: "Today · 10:42", type: "trade", title: "Order executed", detail: "BUY 2 lots NIFTY 24500 CE @ ₹182", actor: "System · OMS" },
      { id: "t2", ts: "Today · 09:15", type: "whatsapp", title: "WhatsApp received", detail: "\"Please activate MCX for me\"", actor: "Client" },
      { id: "t3", ts: "Yesterday", type: "kyc", title: "Commodity proof requested", detail: "6-month bank statement / ITR needed for MCX", actor: "Aditya Nair (RM)" },
      { id: "t4", ts: "12 Sep 2026", type: "stage", title: "Moved to Active Trader", detail: "First funded trade settled T+1", actor: "System" },
    ],
    created_at: "2026-08-28",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1043",
    name: "Sneha Reddy",
    pan: "BNZPS7712K",
    email: "sneha.reddy@outlook.com",
    phone: "+91 99012 41043",
    city: "Hyderabad",
    branch_id: "hyd",
    rm_id: "RM-105",
    stage: "Segment Activation",
    priority: "HOT",
    plan_id: "wealth",
    source: "Referral — Existing Client",
    kyc_status: "Verified",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: true,
    esign_done: true,
    demat_no: "IN301774 · 20551903",
    segments_active: ["equity_delivery", "mf_sip"],
    segments_requested: ["fno", "currency"],
    aum: 960000,
    margin_available: 210000,
    brokerage_mtd: 8600,
    sip_active: true,
    sip_amount: 15000,
    disposition: "interested",
    last_activity: "F&O activation form e-signed",
    notes: "Wants F&O + Currency. Derivatives eligibility (income proof) verified, awaiting exchange UCC update.",
    timeline: [
      { id: "t1", ts: "Today · 11:20", type: "stage", title: "F&O activation submitted", detail: "Income proof accepted, UCC update queued", actor: "Sandeep Rao (RM)" },
      { id: "t2", ts: "Yesterday", type: "call", title: "Advisory call — 8m 12s", detail: "Explained F&O risk disclosure & margins", actor: "Sandeep Rao (RM)" },
      { id: "t3", ts: "18 Sep 2026", type: "system", title: "SIP registered", detail: "₹15,000/mo — 3 direct equity funds", actor: "System" },
    ],
    created_at: "2026-09-05",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1044",
    name: "Arjun Menon",
    pan: "CQRPM1198D",
    email: "arjun.menon@gmail.com",
    phone: "+91 98450 41044",
    city: "Bengaluru",
    branch_id: "blr",
    rm_id: "RM-102",
    stage: "E-Sign",
    priority: "WARM",
    plan_id: "value",
    source: "Instagram Ad — Zero Brokerage",
    kyc_status: "IPV Done",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: false,
    esign_done: false,
    demat_no: null,
    segments_active: [],
    segments_requested: ["equity_delivery", "mf_sip"],
    aum: 0,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "docs_pending",
    last_activity: "AOF e-Sign link sent on WhatsApp",
    notes: "Nominee declaration pending. E-Sign OTP link dispatched — follow up for completion.",
    timeline: [
      { id: "t1", ts: "Today · 09:50", type: "whatsapp", title: "E-Sign link sent", detail: "Aadhaar OTP e-Sign link delivered to client", actor: "System · WhatsApp" },
      { id: "t2", ts: "Yesterday", type: "kyc", title: "IPV completed", detail: "Video IPV geo-tagged & timestamped", actor: "Priya Iyer (RM)" },
      { id: "t3", ts: "20 Sep 2026", type: "stage", title: "Bank penny-drop verified", detail: "HDFC ****4471 name match 100%", actor: "System" },
    ],
    created_at: "2026-09-18",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1045",
    name: "Pooja Sharma",
    pan: "DLMPS9043H",
    email: "pooja.sharma91@gmail.com",
    phone: "+91 98110 41045",
    city: "New Delhi",
    branch_id: "del",
    rm_id: "RM-104",
    stage: "Bank & Nominee",
    priority: "WARM",
    plan_id: "wealth",
    source: "Website — SIP Calculator",
    kyc_status: "IPV Done",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: false,
    nominee_added: false,
    esign_done: false,
    demat_no: null,
    segments_active: [],
    segments_requested: ["mf_sip", "equity_delivery"],
    aum: 0,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "docs_pending",
    last_activity: "Bank penny-drop failed — name mismatch",
    notes: "Penny-drop name mismatch (maiden name). Requested cancelled cheque upload. Keen SIP investor.",
    timeline: [
      { id: "t1", ts: "Today · 08:30", type: "system", title: "Penny-drop failed", detail: "Name mismatch — cancelled cheque requested", actor: "System · Bank API" },
      { id: "t2", ts: "Yesterday", type: "call", title: "Onboarding call — 5m 40s", detail: "Walked through goal-based SIP plan", actor: "Neha Agarwal (RM)" },
    ],
    created_at: "2026-09-19",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1046",
    name: "Imran Sheikh",
    pan: "EPQPS3321M",
    email: "imran.sheikh@yahoo.com",
    phone: "+91 99200 41046",
    city: "Mumbai",
    branch_id: "mum",
    rm_id: "RM-103",
    stage: "KYC (PAN / Aadhaar / IPV)",
    priority: "HOT",
    plan_id: "active",
    source: "Referral — IB Partner",
    kyc_status: "PAN Verified",
    aadhaar_linked: true,
    ipv_done: false,
    bank_verified: false,
    nominee_added: false,
    esign_done: false,
    demat_no: null,
    segments_active: [],
    segments_requested: ["fno", "intraday"],
    aum: 0,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "callback",
    last_activity: "IPV scheduled for 4:00 PM",
    notes: "Experienced F&O trader migrating from another broker. Wants same-day activation. IPV slot booked.",
    timeline: [
      { id: "t1", ts: "Today · 12:05", type: "call", title: "Callback booked", detail: "Video IPV scheduled 4:00 PM IST", actor: "Vikram Malhotra (RM)" },
      { id: "t2", ts: "Today · 10:00", type: "kyc", title: "PAN + Aadhaar verified", detail: "PAN–Aadhaar seeding confirmed via NSDL", actor: "System" },
    ],
    created_at: "2026-09-21",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1047",
    name: "Lakshmi Narayanan",
    pan: "FRSPN8890J",
    email: "lakshmi.n@gmail.com",
    phone: "+91 98400 41047",
    city: "Chennai",
    branch_id: "blr",
    rm_id: "RM-101",
    stage: "New Enquiry",
    priority: "WARM",
    plan_id: "value",
    source: "Google — Open Demat Account",
    kyc_status: "Pending",
    aadhaar_linked: false,
    ipv_done: false,
    bank_verified: false,
    nominee_added: false,
    esign_done: false,
    demat_no: null,
    segments_active: [],
    segments_requested: ["equity_delivery", "mf_sip"],
    aum: 0,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "interested",
    last_activity: "Requested account opening on website",
    notes: "First-time investor. Sent welcome WhatsApp with account-opening link and PAN capture form.",
    timeline: [
      { id: "t1", ts: "Today · 13:10", type: "whatsapp", title: "Welcome message sent", detail: "Account-opening link + PAN capture form", actor: "System · WhatsApp" },
      { id: "t2", ts: "Today · 13:08", type: "system", title: "Lead captured", detail: "Web form — 'Open Demat Account'", actor: "System" },
    ],
    created_at: "2026-09-22",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1048",
    name: "Deepak Chauhan",
    pan: "GTVPC1076N",
    email: "deepak.chauhan@gmail.com",
    phone: "+91 99999 41048",
    city: "Ahmedabad",
    branch_id: "ahm",
    rm_id: "RM-106",
    stage: "Account Activated",
    priority: "WARM",
    plan_id: "value",
    source: "Referral — Existing Client",
    kyc_status: "Verified",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: true,
    esign_done: true,
    demat_no: "IN302902 · 21874550",
    segments_active: ["equity_delivery", "intraday"],
    segments_requested: ["mf_sip"],
    aum: 120000,
    margin_available: 45000,
    brokerage_mtd: 1200,
    sip_active: false,
    sip_amount: 0,
    disposition: "interested",
    last_activity: "Account activated — awaiting first funding",
    notes: "Account live but not yet funded. Nudge campaign for first deposit + SIP cross-sell scheduled.",
    timeline: [
      { id: "t1", ts: "Today · 11:00", type: "stage", title: "Account activated", detail: "UCC mapped at NSE & BSE", actor: "System" },
      { id: "t2", ts: "Yesterday", type: "note", title: "Cross-sell tagged", detail: "Eligible for SIP starter nudge", actor: "Ananya Shah (RM)" },
    ],
    created_at: "2026-09-16",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1049",
    name: "Fatima Ansari",
    pan: "HWXPA5540P",
    email: "fatima.ansari@gmail.com",
    phone: "+91 98190 41049",
    city: "Mumbai",
    branch_id: "mum",
    rm_id: "RM-103",
    stage: "Active Trader",
    priority: "HOT",
    plan_id: "active",
    source: "YouTube — Options Strategy",
    kyc_status: "Verified",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: true,
    esign_done: true,
    demat_no: "IN300214 · 19903318",
    segments_active: ["equity_delivery", "intraday", "fno", "currency"],
    segments_requested: [],
    aum: 2760000,
    margin_available: 1150000,
    brokerage_mtd: 41200,
    sip_active: true,
    sip_amount: 50000,
    disposition: "funded",
    last_activity: "Top brokerage contributor this month",
    notes: "Premium F&O + Currency trader. Highest brokerage MTD at Mumbai branch. Eligible for priority desk.",
    timeline: [
      { id: "t1", ts: "Today · 14:22", type: "trade", title: "Bracket order filled", detail: "SELL 5 lots BANKNIFTY 51000 PE", actor: "System · OMS" },
      { id: "t2", ts: "Today · 09:40", type: "call", title: "Priority desk check-in", detail: "Margin utilisation & expiry rollover plan", actor: "Vikram Malhotra (RM)" },
    ],
    created_at: "2026-07-14",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1050",
    name: "Harpreet Singh",
    pan: "JKLPS2287Q",
    email: "harpreet.singh@gmail.com",
    phone: "+91 98150 41050",
    city: "New Delhi",
    branch_id: "del",
    rm_id: "RM-104",
    stage: "Dormant / Recall",
    priority: "COLD",
    plan_id: "value",
    source: "Google — Free Demat",
    kyc_status: "Verified",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: true,
    esign_done: true,
    demat_no: "IN301774 · 17720094",
    segments_active: ["equity_delivery"],
    segments_requested: [],
    aum: 38000,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "callback",
    last_activity: "No trades in 128 days",
    notes: "Dormant account. Re-activation offer (free AMC year) queued via WhatsApp + call recall.",
    timeline: [
      { id: "t1", ts: "Today · 08:00", type: "system", title: "Recall campaign queued", detail: "Free-AMC re-activation offer", actor: "System · Automation" },
      { id: "t2", ts: "17 May 2026", type: "trade", title: "Last trade", detail: "SELL 10 TATAMOTORS @ ₹1,024", actor: "System" },
    ],
    created_at: "2026-02-11",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1051",
    name: "Ananya Ghosh",
    pan: "KMNPG6612R",
    email: "ananya.ghosh@gmail.com",
    phone: "+91 98300 41051",
    city: "Kolkata",
    branch_id: "blr",
    rm_id: "RM-102",
    stage: "Segment Activation",
    priority: "WARM",
    plan_id: "wealth",
    source: "Website — IPO Alerts",
    kyc_status: "Verified",
    aadhaar_linked: true,
    ipv_done: true,
    bank_verified: true,
    nominee_added: true,
    esign_done: true,
    demat_no: "IN302902 · 22019984",
    segments_active: ["equity_delivery", "mf_sip"],
    segments_requested: ["fno"],
    aum: 540000,
    margin_available: 95000,
    brokerage_mtd: 3900,
    sip_active: true,
    sip_amount: 20000,
    disposition: "docs_pending",
    last_activity: "F&O income proof under review",
    notes: "Requested F&O activation. 6-month bank statement uploaded — under risk-desk review for eligibility.",
    timeline: [
      { id: "t1", ts: "Today · 10:15", type: "kyc", title: "F&O proof uploaded", detail: "6-month bank statement submitted", actor: "Client · App" },
      { id: "t2", ts: "Yesterday", type: "whatsapp", title: "IPO allotment update", detail: "Applied 2 IPOs via UPI mandate", actor: "System" },
    ],
    created_at: "2026-08-30",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1052",
    name: "Manish Gupta",
    pan: "LPQPG4408S",
    email: "manish.gupta@gmail.com",
    phone: "+91 99710 41052",
    city: "Hyderabad",
    branch_id: "hyd",
    rm_id: "RM-105",
    stage: "New Enquiry",
    priority: "COLD",
    plan_id: "active",
    source: "Facebook Ad — Intraday",
    kyc_status: "Pending",
    aadhaar_linked: false,
    ipv_done: false,
    bank_verified: false,
    nominee_added: false,
    esign_done: false,
    demat_no: null,
    segments_active: [],
    segments_requested: ["intraday", "fno"],
    aum: 0,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "callback",
    last_activity: "Enquiry — comparing brokers",
    notes: "Price-sensitive intraday trader comparing brokerage plans. Sent plan comparison + ₹15 flat offer.",
    timeline: [
      { id: "t1", ts: "Today · 12:40", type: "whatsapp", title: "Plan comparison sent", detail: "Active Trader ₹15 flat brokerage offer", actor: "System · WhatsApp" },
      { id: "t2", ts: "Today · 12:30", type: "system", title: "Lead captured", detail: "Facebook lead form — Intraday", actor: "System" },
    ],
    created_at: "2026-09-22",
    updated_at: "2026-09-22",
  },
  {
    client_code: "JS-BR-1053",
    name: "Kavya Pillai",
    pan: "MRTPP7735T",
    email: "kavya.pillai@gmail.com",
    phone: "+91 98470 41053",
    city: "Ahmedabad",
    branch_id: "ahm",
    rm_id: "RM-106",
    stage: "KYC (PAN / Aadhaar / IPV)",
    priority: "WARM",
    plan_id: "wealth",
    source: "Referral — Existing Client",
    kyc_status: "PAN Verified",
    aadhaar_linked: true,
    ipv_done: false,
    bank_verified: false,
    nominee_added: false,
    esign_done: false,
    demat_no: null,
    segments_active: [],
    segments_requested: ["equity_delivery", "mf_sip"],
    aum: 0,
    margin_available: 0,
    brokerage_mtd: 0,
    sip_active: false,
    sip_amount: 0,
    disposition: "interested",
    last_activity: "Aadhaar e-KYC OTP verified",
    notes: "Goal-based investor (child education SIP). IPV pending — self-KYC link resent.",
    timeline: [
      { id: "t1", ts: "Today · 11:35", type: "kyc", title: "Aadhaar e-KYC done", detail: "OTP verified via UIDAI", actor: "System" },
      { id: "t2", ts: "Today · 11:20", type: "note", title: "Goal tagged", detail: "Child education — 12yr SIP horizon", actor: "Ananya Shah (RM)" },
    ],
    created_at: "2026-09-21",
    updated_at: "2026-09-22",
  },
];

/** Returns a deep clone of the seed clients with stable ids (1..n). */
export function cloneSeedLeads(): BrokingLead[] {
  return RAW_SEED.map((lead, idx) => ({
    id: idx + 1,
    ...lead,
    segments_active: [...lead.segments_active],
    segments_requested: [...lead.segments_requested],
    timeline: lead.timeline.map((t) => ({ ...t })),
  }));
}

/* ------------------------------------------------------------------ *
 * Derived dashboard stats (MIS)
 * ------------------------------------------------------------------ */

export interface BrokingStats {
  totalEnquiries: number;
  accountsOpenedMtd: number;
  kycPending: number;
  segmentActivations: number;
  activeClients: number;
  aum: number;
  brokerageMtd: number;
  conversion: number; // %
  dormant: number;
}

export function computeStats(leads: BrokingLead[]): BrokingStats {
  const totalEnquiries = leads.length;
  const activated = leads.filter((l) =>
    ["Account Activated", "Segment Activation", "Active Trader"].includes(l.stage)
  );
  const kycPending = leads.filter((l) =>
    ["New Enquiry", "KYC (PAN / Aadhaar / IPV)", "Bank & Nominee", "E-Sign"].includes(l.stage)
  ).length;
  const segmentActivations = leads.reduce((sum, l) => sum + l.segments_active.length, 0);
  const activeClients = leads.filter((l) => l.stage === "Active Trader").length;
  const aum = leads.reduce((sum, l) => sum + l.aum, 0);
  const brokerageMtd = leads.reduce((sum, l) => sum + l.brokerage_mtd, 0);
  const dormant = leads.filter((l) => l.stage === "Dormant / Recall").length;
  const conversion = totalEnquiries ? Math.round((activeClients / totalEnquiries) * 100) : 0;

  return {
    totalEnquiries,
    accountsOpenedMtd: activated.length,
    kycPending,
    segmentActivations,
    activeClients,
    aum,
    brokerageMtd,
    conversion,
    dormant,
  };
}

/* ------------------------------------------------------------------ *
 * Reminders / automated follow-ups
 * ------------------------------------------------------------------ */

export interface Reminder {
  id: string;
  title: string;
  clientCode: string;
  clientName: string;
  rmId: string;
  branchId: string;
  channel: "call" | "whatsapp" | "email";
  due: string;
  status: "overdue" | "due" | "upcoming" | "done";
  auto: boolean; // triggered by automation vs manual
}

export const SEED_REMINDERS: Reminder[] = [
  { id: "rem-1", title: "Complete E-Sign follow-up", clientCode: "JS-BR-1044", clientName: "Arjun Menon", rmId: "RM-102", branchId: "blr", channel: "whatsapp", due: "Today · 3:00 PM", status: "due", auto: true },
  { id: "rem-2", title: "Bank cheque re-upload chase", clientCode: "JS-BR-1045", clientName: "Pooja Sharma", rmId: "RM-104", branchId: "del", channel: "call", due: "Today · 4:30 PM", status: "due", auto: false },
  { id: "rem-3", title: "IPV video verification", clientCode: "JS-BR-1046", clientName: "Imran Sheikh", rmId: "RM-103", branchId: "mum", channel: "call", due: "Today · 4:00 PM", status: "upcoming", auto: false },
  { id: "rem-4", title: "First-funding nudge", clientCode: "JS-BR-1048", clientName: "Deepak Chauhan", rmId: "RM-106", branchId: "ahm", channel: "whatsapp", due: "Tomorrow · 10:00 AM", status: "upcoming", auto: true },
  { id: "rem-5", title: "Dormant recall — free AMC offer", clientCode: "JS-BR-1050", clientName: "Harpreet Singh", rmId: "RM-104", branchId: "del", channel: "call", due: "Overdue · 2 days", status: "overdue", auto: true },
  { id: "rem-6", title: "F&O risk-desk decision", clientCode: "JS-BR-1051", clientName: "Ananya Ghosh", rmId: "RM-102", branchId: "blr", channel: "email", due: "Tomorrow · 12:00 PM", status: "upcoming", auto: false },
  { id: "rem-7", title: "MCX commodity proof review", clientCode: "JS-BR-1042", clientName: "Rajesh Kumar", rmId: "RM-101", branchId: "blr", channel: "whatsapp", due: "Today · 5:15 PM", status: "due", auto: true },
];

/* ------------------------------------------------------------------ *
 * Click-to-call log (telephony integration-ready)
 * ------------------------------------------------------------------ */

export interface CallLogEntry {
  id: string;
  clientName: string;
  clientCode: string;
  phone: string;
  rmName: string;
  direction: "outbound" | "inbound";
  outcome: "connected" | "missed" | "callback" | "voicemail";
  duration: string;
  time: string;
  recording: boolean;
}

export const SEED_CALL_LOG: CallLogEntry[] = [
  { id: "cl-1", clientName: "Fatima Ansari", clientCode: "JS-BR-1049", phone: "+91 98190 41049", rmName: "Vikram Malhotra", direction: "outbound", outcome: "connected", duration: "9m 40s", time: "Today · 09:40", recording: true },
  { id: "cl-2", clientName: "Imran Sheikh", clientCode: "JS-BR-1046", phone: "+91 99200 41046", rmName: "Vikram Malhotra", direction: "inbound", outcome: "callback", duration: "2m 05s", time: "Today · 12:05", recording: true },
  { id: "cl-3", clientName: "Pooja Sharma", clientCode: "JS-BR-1045", phone: "+91 98110 41045", rmName: "Neha Agarwal", direction: "outbound", outcome: "connected", duration: "5m 40s", time: "Yesterday · 17:20", recording: true },
  { id: "cl-4", clientName: "Manish Gupta", clientCode: "JS-BR-1052", phone: "+91 99710 41052", rmName: "Sandeep Rao", direction: "outbound", outcome: "voicemail", duration: "0m 22s", time: "Today · 11:10", recording: false },
  { id: "cl-5", clientName: "Harpreet Singh", clientCode: "JS-BR-1050", phone: "+91 98150 41050", rmName: "Neha Agarwal", direction: "outbound", outcome: "missed", duration: "—", time: "Today · 08:05", recording: false },
];

/* ------------------------------------------------------------------ *
 * Incoming WhatsApp message pool (real-time popup simulation)
 * ------------------------------------------------------------------ */

export interface IncomingWhatsApp {
  clientCode: string;
  name: string;
  phone: string;
  preview: string;
  kind: "kyc" | "activation" | "margin" | "general" | "sip";
}

export const INCOMING_WHATSAPP_POOL: IncomingWhatsApp[] = [
  { clientCode: "JS-BR-1042", name: "Rajesh Kumar", phone: "+91 98860 41042", preview: "Is my MCX commodity segment activated yet?", kind: "activation" },
  { clientCode: "JS-BR-1046", name: "Imran Sheikh", phone: "+91 99200 41046", preview: "I've uploaded my Aadhaar. What's next for IPV?", kind: "kyc" },
  { clientCode: "JS-BR-1049", name: "Fatima Ansari", phone: "+91 98190 41049", preview: "What's my available margin for tomorrow's expiry?", kind: "margin" },
  { clientCode: "JS-BR-1051", name: "Ananya Ghosh", phone: "+91 98300 41051", preview: "Did my IPO application go through on UPI?", kind: "general" },
  { clientCode: "JS-BR-1053", name: "Kavya Pillai", phone: "+91 98470 41053", preview: "Can you help me start a SIP for my child's education?", kind: "sip" },
  { clientCode: "JS-BR-1044", name: "Arjun Menon", phone: "+91 98450 41044", preview: "I completed the e-Sign just now ✅", kind: "kyc" },
  { clientCode: "JS-BR-1043", name: "Sneha Reddy", phone: "+91 99012 41043", preview: "When can I place my first F&O order?", kind: "activation" },
];

/** Canned broking WhatsApp templates for the chat drawer. */
export const WHATSAPP_TEMPLATES: { id: string; label: string; body: string }[] = [
  { id: "kyc_reminder", label: "KYC reminder", body: "Hi {name}, your demat account KYC is almost done! Please complete your pending IPV / e-Sign to start trading. Tap the secure link we sent to finish in 2 minutes. — Team Johnson Securities" },
  { id: "account_active", label: "Account active", body: "🎉 Congratulations {name}! Your demat & trading account ({code}) is now ACTIVE. NSE·BSE UCC mapped. Fund your account to place your first order. Happy investing!" },
  { id: "fno_activation", label: "F&O activated", body: "Hi {name}, your F&O (Futures & Options) segment is now enabled. Please trade responsibly — derivatives carry higher risk. Your dealing desk RM is available for support." },
  { id: "margin_alert", label: "Margin alert", body: "⚠️ Margin alert for {name}: your utilised margin has crossed 80%. Please add funds or reduce positions before market close to avoid auto square-off." },
  { id: "settlement", label: "Settlement note", body: "Hi {name}, your trades have been settled (T+1). Funds/securities are reflected in your ledger. View your contract note in the app under Reports → Tax P&L." },
  { id: "sip_nudge", label: "SIP nudge", body: "Hi {name}, start a Systematic Investment Plan from just ₹500/month in direct mutual funds — zero commission. Reply START and your RM will set it up today." },
];

export function fillTemplate(body: string, lead: { name: string; client_code: string }): string {
  return body.replace(/\{name\}/g, lead.name.split(" ")[0]).replace(/\{code\}/g, lead.client_code);
}

/* ------------------------------------------------------------------ *
 * Compliance / audit (SEBI-oriented)
 * ------------------------------------------------------------------ */

export interface AuditLogEntry {
  id: string;
  ts: string;
  actor: string;
  role: CrmRole;
  action: string;
  entity: string;
  ip: string;
  severity: "info" | "warning" | "critical";
}

export const SEED_AUDIT_LOG: AuditLogEntry[] = [
  { id: "a1", ts: "2026-09-22 14:22:10", actor: "Aditya Nair", role: "RM", action: "Viewed masked PAN (last 4 only)", entity: "Client JS-BR-1042", ip: "10.4.2.19", severity: "info" },
  { id: "a2", ts: "2026-09-22 13:05:44", actor: "System · Automation", role: "Admin", action: "Dispatched WhatsApp KYC reminder", entity: "Client JS-BR-1044", ip: "—", severity: "info" },
  { id: "a3", ts: "2026-09-22 11:35:02", actor: "System · UIDAI", role: "Admin", action: "Aadhaar e-KYC OTP verified", entity: "Client JS-BR-1053", ip: "—", severity: "info" },
  { id: "a4", ts: "2026-09-22 10:15:31", actor: "Priya Iyer", role: "RM", action: "Uploaded F&O income proof to vault", entity: "Client JS-BR-1051", ip: "10.4.2.31", severity: "info" },
  { id: "a5", ts: "2026-09-22 09:48:12", actor: "Meera Krishnan", role: "Management", action: "Exported branch MIS report (CSV)", entity: "Branch BR-BLR-01", ip: "10.4.1.5", severity: "warning" },
  { id: "a6", ts: "2026-09-21 18:20:55", actor: "Unknown", role: "RM", action: "Failed login — 3 attempts, account locked", entity: "User rm-guest", ip: "103.21.44.9", severity: "critical" },
  { id: "a7", ts: "2026-09-21 16:02:08", actor: "Vikram Malhotra", role: "RM", action: "Full PAN unmask requested & denied (role scope)", entity: "Client JS-BR-1049", ip: "10.4.3.12", severity: "warning" },
];

/* ------------------------------------------------------------------ *
 * Small formatting helpers
 * ------------------------------------------------------------------ */

export function formatINR(value: number): string {
  return "₹" + value.toLocaleString("en-IN");
}

/** Compact Indian currency (Lakh / Crore) for tiles. */
export function formatINRCompact(value: number): string {
  if (value >= 10000000) return "₹" + (value / 10000000).toFixed(2) + " Cr";
  if (value >= 100000) return "₹" + (value / 100000).toFixed(2) + " L";
  if (value >= 1000) return "₹" + (value / 1000).toFixed(1) + "K";
  return "₹" + value.toLocaleString("en-IN");
}

/** Mask a PAN for RM role (ABCDE1234F -> XXXXX1234F). */
export function maskPan(pan: string, role: CrmRole): string {
  if (role === "Admin") return pan;
  if (pan.length < 6) return "XXXXXXXXXX";
  return "XXXXX" + pan.slice(5);
}
